import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hors connexion, le client Convex met la mutation en file et sa promesse ne
 * rejette jamais : sans garde, l'indicateur resterait sur « Enregistrement… »
 * indéfiniment. Passé ce délai on avoue l'échec ; si la file finit par partir,
 * le retour de la mutation remet l'état à « Enregistré ».
 */
const DELAI_GARDE_MS = 10_000;

export type EtatSauvegarde = "inactif" | "en-cours" | "enregistre" | "erreur";

/**
 * Autosave débouncé qui EXPOSE son état. Sans lui, un enregistrement raté ne
 * laissait qu'une ligne en console : l'utilisateur croyait ses réponses à l'abri
 * et les perdait en fermant l'onglet.
 *
 * `sauver` doit rejeter (ou lever) en cas d'échec — une mutation qui renvoie
 * `{ erreur }` au lieu de lever doit être convertie par l'appelant.
 */
export function useSauvegardeDifferee<T>(
  sauver: (valeur: T) => Promise<unknown>,
  delaiMs: number,
) {
  const [etat, setEtat] = useState<EtatSauvegarde>("inactif");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const derniere = useRef<{ valeur: T } | null>(null);
  const sauverCourant = useRef(sauver);
  // Numéro de la dernière écriture lancée : une réponse qui revient après une
  // écriture plus récente ne doit pas réafficher un état périmé.
  const generation = useRef(0);

  useEffect(() => {
    sauverCourant.current = sauver;
  }, [sauver]);

  const executer = useCallback(async (valeur: T) => {
    const numero = ++generation.current;
    setEtat("en-cours");
    const garde = setTimeout(() => {
      if (numero === generation.current) setEtat("erreur");
    }, DELAI_GARDE_MS);
    try {
      await sauverCourant.current(valeur);
      if (numero === generation.current) setEtat("enregistre");
    } catch (e) {
      console.error("[autosave] Enregistrement impossible", e);
      if (numero === generation.current) setEtat("erreur");
    } finally {
      clearTimeout(garde);
    }
  }, []);

  const planifier = useCallback(
    (valeur: T) => {
      derniere.current = { valeur };
      // Une écriture déjà en vol porte un état désormais périmé : son retour ne
      // doit pas afficher « Enregistré » tant que celle-ci n'est pas partie.
      generation.current++;
      if (timer.current) clearTimeout(timer.current);
      setEtat("en-cours");
      timer.current = setTimeout(() => {
        // Plus rien en attente : la sortie de page ne doit pas renvoyer ceci.
        timer.current = null;
        void executer(valeur);
      }, delaiMs);
    },
    [delaiMs, executer],
  );

  const reessayer = useCallback(() => {
    if (derniere.current) void executer(derniere.current.valeur);
  }, [executer]);

  // Une écriture encore en attente PART au lieu d'être abandonnée quand on quitte :
  // fermer l'onglet moins de 800 ms après le dernier geste perdait ce geste.
  // `visibilitychange` couvre le mobile, où `pagehide` n'est pas toujours émis.
  useEffect(() => {
    const vider = () => {
      if (!timer.current || !derniere.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      void executer(derniere.current.valeur);
    };
    const surVisibilite = () => {
      if (document.visibilityState === "hidden") vider();
    };
    window.addEventListener("pagehide", vider);
    document.addEventListener("visibilitychange", surVisibilite);
    return () => {
      window.removeEventListener("pagehide", vider);
      document.removeEventListener("visibilitychange", surVisibilite);
      vider();
    };
  }, [executer]);

  /** À appeler avant une soumission : elle porte déjà l'état complet. */
  const annuler = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  return { etat, planifier, reessayer, annuler };
}
